import React, { useState, useEffect } from 'react';
import { Tabs, Tab, TabTitleText } from '@patternfly/react-core';
import LabControlsStore, { labControlsContext } from 'stores/LabControls';
import { observer, useLocalStore } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import LabStore from 'stores/Lab';
import { Helmet } from 'react-helmet';
import { withErrorBoundary } from 'react-error-boundary';
import { handleError } from 'services/ErrorService';
import useIntercomHideReason from 'hooks/useIntercomHideReason';
import usePendoHideReason from 'hooks/usePendoHideReason';
import useMobxStores from 'hooks/useMobxStores';
import GenericViewErrorV2 from 'components/Error/GenericViewErrorV2';
import ErrorState from 'components/Error/ErrorState';
import {
  HUDDirection,
  HUDMainItemMode,
  MAIN_ITEM_ID,
  useFeature,
  useLocalStorageState,
  useScrollToElement,
} from '@ole-ui/ole-ui-components';
import { EnablementFeatureFlags } from 'config/featureFlags';

// tab related components
import LabPolling from 'components/LabPolling';
import LabEnvironmentMobileAlert from 'components/LabEnvironment/MobileAlert';
import LabEnvironment from 'components/LabEnvironment';

import AIAssistantCourseStore, {
  aiAssistantContext,
} from 'components/Course/AiAssist/store';

import CourseStore from './store';
import './styles.scss';

// loading skeleton components
import PageSkeletonLoading from '../skeletons/PageSkeletonLoading';
import ToolbarSkeletonLoading from '../skeletons/ToolbarSkeletonLoading';
import CourseHeaderSkeletonLoading from '../skeletons/CourseHeaderSkeletonLoading';

// layout components
import CourseHeader from './CourseHeader';
import CourseToolbar from './CourseToolbar';
import CourseHUD from './CourseHUD';
import AiAssistMobileOverlay from './AiAssistMobileOverlay';

// tab related components (local)
import CourseTab from './Tabs/CourseTab';
import ExpertExtrasTab from './Tabs/ExpertExtrasTab';
import LabPlusTab from './Tabs/LabPlusTab';

import useCourseCompletionNotification from '../useCourseCompletionNotification';
import useLoadCourse from './hooks/useLoadCourse';
import usePageChange from './hooks/usePageChange';
import useAutoMoveIntercom from './hooks/useAutoMoveIntercom';
import useSidebarState from './hooks/useSidebarState';

enum CourseTabKey {
  Overview = 'overview',
  Toc = 'toc',
  Course = 'course',
  OnlineLab = 'online_lab',
  LabEnv = 'lab_env',
  ExpertExtras = 'expert_extras',
  LabPlus = 'lab_plus',
  Bookmarks = 'bookmarks',
}

const CourseView = () => {
  const { t, i18n } = useTranslation();
  const isEnglishPage = i18n.language?.startsWith('en') ?? true;
  const {
    routerStore,
    progressStore,
    uiStore,
    userStore,
    classesStore,
    eventStore,
    catalogStore,
    configStore,
    featuresStore,
  } = useMobxStores();
  const [activeTabKey, setActiveTabKey] = useState<CourseTabKey>(
    CourseTabKey.Course,
  );

  // initialize local stores
  const labControlsStore = useLocalStore(() => new LabControlsStore());
  const labStore = useLocalStore(() => new LabStore(userStore));
  const courseStore = useLocalStore(
    () =>
      new CourseStore(
        routerStore,
        uiStore,
        userStore,
        catalogStore,
        progressStore,
        classesStore,
        labStore,
        eventStore,
        configStore,
        featuresStore,
      ),
  );

  const { labBlueprints, labDefinition } = labStore;

  // getting route params
  const { route } = routerStore;
  const { course: courseSlug, page: pageIdParam } = (route as any).params;

  const {
    isBundledCourse,
    isLoadingInitialData,
    canShowLabPlusTab,
    canShowExpertExtras,
    courseTotalProgress,
    isLesson,
  } = courseStore;

  const [direction, setDirection] = useState(HUDDirection.UPWARD);
  const handleDirectionChange = (hudDirection: HUDDirection) => {
    setDirection(hudDirection);
  };

  const [isHoveringVideoPlayer, setIsHoveringVideoPlayer] = useState(false);

  // Track when any HUD panel is open so we can suppress floating overlays (e.g. AI quick menu).
  const [activeHUDPanel, setActiveHUDPanel] = useState<string | null>(null);

  // Track the current Table of Contents (HUD MainItem) display mode so we can
  // suppress the AI quick menu only when appropriate.
  const [tocMode, setTocMode] = useState<HUDMainItemMode>(HUDMainItemMode.FULL);

  const isAiAssistEnabled = useFeature({
    section: 'enablements',
    flagName: EnablementFeatureFlags.AIAssistantEnabled,
    defaultFlagValue: false,
  });

  const aiAssistantStore = useLocalStore(
    () =>
      new AIAssistantCourseStore(
        {
          getUuid: () => courseStore.euuid,
          getSlug: () => {
            const code = courseStore.courseCode;
            const version = courseStore.courseVersion;
            const pageTag = courseStore.page?.page_tag;
            if (!code || !version || !pageTag) return null;
            return `${code}/${version}/${pageTag}`;
          },
        },
        t,
      ),
  );

  useEffect(() => () => aiAssistantStore.dispose(), [aiAssistantStore]);

  useEffect(() => {
    if (isAiAssistEnabled && userStore.isLoggedIn) {
      userStore.getUserIdSettings().catch(() => {});
    }
  }, [isAiAssistEnabled, userStore.isLoggedIn]);

  const {
    sideToCRefLeft,
    sideToCRefRight,
    sideToCRef,
    aiAssistRef,
    sidebarPosition,
    setSidebarPosition,
    isSideToCActive,
    setSideToCActive,
    isAiQuickMenuEnabled,
    setIsAiQuickMenuEnabled,
  } = useSidebarState({
    isAiAssistEnabled,
    aiAssistantStore,
  });

  // Suppress the quick menu whenever any HUD panel is open *except* when the
  // Table of Contents is open in "Minimized" mode (where overlays should coexist).
  const isTocOpenInMinimizedMode =
    tocMode === HUDMainItemMode.MINIMIZED && activeHUDPanel === MAIN_ITEM_ID;
  const isAiQuickMenuSuppressed =
    (Boolean(activeHUDPanel) && !isTocOpenInMinimizedMode) ||
    isSideToCActive ||
    !isEnglishPage;
  const [isVideoPlayerEnabled, setIsVideoPlayerEnabled] = useLocalStorageState(
    'course-hud-video-player-enabled',
    true,
  );

  const handleVideoPlayerChange = (enabled: boolean) => {
    setIsVideoPlayerEnabled(enabled);
  };

  useAutoMoveIntercom();
  useIntercomHideReason('course:ai-assistant', aiAssistantStore.isOpen);
  usePendoHideReason('course:ai-assistant', aiAssistantStore.isOpen);

  // load course and complementary data
  useLoadCourse(courseSlug, courseStore);

  useEffect(() => {
    if (!isBundledCourse || !courseSlug) return;

    routerStore.navigate(
      'courses:overview',
      { course: courseSlug },
      // @ts-expect-error router5 options
      { replace: true },
    );
  }, [isBundledCourse, courseSlug, routerStore]);

  const [courseHeaderRef, scrollToCourseHeader] =
    useScrollToElement<HTMLDivElement>();

  // change page content on page change
  usePageChange(pageIdParam, courseStore, () => {
    setActiveTabKey(CourseTabKey.Course);
    scrollToCourseHeader();
  });

  useCourseCompletionNotification(
    courseStore.canShowCourseCompletionNotification,
    courseTotalProgress,
    courseSlug,
  );

  const handleTabClick = (_event: any, tabIndex: CourseTabKey) => {
    setActiveTabKey(tabIndex);
  };

  const loadingState = (
    <div className="hud-course-view__content">
      <CourseHeaderSkeletonLoading />
      <ToolbarSkeletonLoading />
      <div style={{ marginTop: '180px' }}>
        <PageSkeletonLoading />
      </div>
    </div>
  );

  if (courseStore.initialLoadError) {
    return (
      <ErrorState
        showExploreSubscriptions={
          userStore.isRHLSTrialSubscriber && userStore.isRedHatLead
        }
        onRetry={() => {
          courseStore.getInitialData(courseSlug);
        }}
        onSupportClick={() => {
          eventStore.toggleDrawer('support_form');
        }}
        initialErrorCount={courseStore.initialErrorCount}
      />
    );
  }

  if (isBundledCourse) {
    return null;
  }

  const courseHUD = (
    <CourseHUD
      sideToCRef={sideToCRef}
      aiAssistRef={aiAssistRef}
      courseStore={courseStore}
      key="course-hud"
      isHoveringVideoPlayer={isHoveringVideoPlayer}
      isVideoPlayerEnabled={isVideoPlayerEnabled}
      onDirectionChange={handleDirectionChange}
      onOverviewButtonClicked={() => {
        setActiveTabKey(CourseTabKey.Overview);
      }}
      isOverviewDisabled={activeTabKey === CourseTabKey.Overview}
      onVideoPlayerChange={handleVideoPlayerChange}
      isSideToCActive={isSideToCActive}
      onActiveHUDPanelChange={setActiveHUDPanel}
      onTocModeChange={setTocMode}
      isAiQuickMenuEnabled={isAiQuickMenuEnabled}
      setIsAiQuickMenuEnabled={setIsAiQuickMenuEnabled}
      setSideToCActive={setSideToCActive}
      sidebarPosition={sidebarPosition}
      setSidebarPosition={setSidebarPosition}
    />
  );

  const content = (
    // <div className="hud-course-view__content">
    <div ref={courseHeaderRef} className="hud-course-view__content">
      <CourseHeader
        courseSlug={courseSlug}
        courseStore={courseStore}
        labControlsStore={labControlsStore}
      />
      {courseStore.loadedInitialData && (
        <>
          <CourseToolbar courseSlug={courseSlug} courseStore={courseStore} />
          <Tabs
            className="hud-course-view__tabs"
            isFilled
            isBox
            activeKey={activeTabKey}
            onSelect={handleTabClick}
            aria-label={t('Course related tabs')}
            role="region"
            isOverflowHorizontal
          >
            <Tab
              eventKey={CourseTabKey.Course}
              title={
                <TabTitleText>
                  {isLesson ? t('Lesson') : t('Course')}
                </TabTitleText>
              }
            >
              <CourseTab
                courseStore={courseStore}
                setIsHoveringVideoPlayer={setIsHoveringVideoPlayer}
                isVideoPlayerEnabled={isVideoPlayerEnabled}
                isAiAssistEnabled={isAiAssistEnabled}
                isAiQuickMenuEnabled={isAiQuickMenuEnabled}
                setIsAiQuickMenuEnabled={setIsAiQuickMenuEnabled}
                isAiQuickMenuSuppressed={isAiQuickMenuSuppressed}
              />
            </Tab>
            {labBlueprints.length && !labDefinition.doc_id && (
              <Tab
                eventKey={CourseTabKey.OnlineLab}
                title={<TabTitleText>{t('Online Lab')}</TabTitleText>}
              >
                <labControlsContext.Provider value={labControlsStore}>
                  <div className="hud-course-view__tab-content">
                    <LabPolling
                      blueprints={labBlueprints}
                      courseSlug={courseSlug}
                    />
                  </div>
                </labControlsContext.Provider>
              </Tab>
            )}
            {labDefinition.doc_id && (
              <Tab
                eventKey={CourseTabKey.LabEnv}
                title={<TabTitleText>{t('Lab Environment')}</TabTitleText>}
              >
                <div
                  id="tab-course-lab-environment"
                  className="hud-course-view__tab-content"
                >
                  <LabEnvironmentMobileAlert />
                  <LabEnvironment
                    courseSlug={courseSlug}
                    labDefinition={labDefinition}
                  />
                </div>
              </Tab>
            )}
            {canShowExpertExtras && (
              <Tab
                eventKey={CourseTabKey.ExpertExtras}
                title={<TabTitleText>{t('Expert Extras')}</TabTitleText>}
              >
                <ExpertExtrasTab playListId={courseStore.playListId} />
              </Tab>
            )}
            {canShowLabPlusTab && (
              <Tab
                eventKey={CourseTabKey.LabPlus}
                title={<TabTitleText>{t('Lab Plus')}</TabTitleText>}
              >
                <LabPlusTab courseStore={courseStore} />
              </Tab>
            )}
          </Tabs>
        </>
      )}
    </div>
    // </div>
  );

  let pageTitle = t('Course - Red Hat Learning');
  if (courseStore.title) {
    if (courseStore.pageSlug) {
      pageTitle = `${courseStore.pageSlug} - ${courseStore.title} - Red Hat Learning`;
    } else {
      pageTitle = `${courseStore.title} - Red Hat Learning`;
    }
  }

  return (
    <aiAssistantContext.Provider
      value={isAiAssistEnabled ? aiAssistantStore : null}
    >
      <div className="hud-course-view">
        <Helmet title={pageTitle} />

        {direction === HUDDirection.DOWNWARD ? courseHUD : null}
        <div className="hud-course-view__content-wrapper">
          {isLoadingInitialData ? loadingState : content}
        </div>
        {direction === HUDDirection.UPWARD ? courseHUD : null}
        <AiAssistMobileOverlay />
        {/* Portals render in fixed anchors above the HUD dock — not in document flow. */}
        <div
          key="sidebar-placeholder-left"
          ref={sideToCRefLeft}
          className="hud-course-view__portal-anchor hud-course-view__portal-anchor--left"
          tabIndex={-1}
        />
        <div
          key="sidebar-placeholder-right"
          ref={sideToCRefRight}
          className="hud-course-view__portal-anchor hud-course-view__portal-anchor--right"
          tabIndex={-1}
        />
      </div>
    </aiAssistantContext.Provider>
  );
};

export default withErrorBoundary(
  observer(CourseView),
  GenericViewErrorV2,
  handleError,
);
